import { format, formatDistanceToNow } from 'date-fns'
import { nanoid } from 'nanoid'
import { AttentionNode, Burden, NodeType, Participant, ProvenanceEvent } from './types'

export const CREATOR_LINKEDIN = 'https://www.linkedin.com/in/mindful-abhay/'
export const CREATOR_COFFEE = 'https://buymeacoffee.com/abhaypratapsingh'

export const STORAGE = {
  CONTEXTS: 'attentio:contexts',
  STATE_PREFIX: 'attentio:state:',
  LIVE_CODE: 'attentio:live-code',
  CONNECTED_WORKER: 'attentio:worker-url',
}

export function generateId(len = 10) {
  return nanoid(len)
}

export function nowIso() {
  return new Date().toISOString()
}

export function formatDate(iso: string) {
  try {
    return format(new Date(iso), 'MMM d, yyyy')
  } catch {
    return iso.slice(0, 10)
  }
}

export function timeAgo(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true })
  } catch {
    return ''
  }
}

export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

export function save<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export function getParticipantColor(index: number) {
  const palette = ['#6b8aa3', '#5c8a6b', '#a38a5e', '#8a6bb3', '#b35c5c', '#5c8a8a']
  return palette[index % palette.length]
}

export function createEvent(
  type: ProvenanceEvent['type'],
  participantId: string,
  payload: Record<string, any>
): ProvenanceEvent {
  return {
    id: generateId(9),
    ts: nowIso(),
    participantId,
    type,
    payload,
  }
}

export function averageBurden(nodes: AttentionNode[]): Partial<Burden> {
  const withB = nodes.filter(n => n.burden)
  if (withB.length === 0) return {}
  const sum = withB.reduce(
    (acc, n) => ({
      cognitive: acc.cognitive + (n.burden?.cognitive || 0),
      emotional: acc.emotional + (n.burden?.emotional || 0),
      logistical: acc.logistical + (n.burden?.logistical || 0),
      relational: acc.relational + (n.burden?.relational || 0),
    }),
    { cognitive: 0, emotional: 0, logistical: 0, relational: 0 }
  )
  const c = withB.length
  return {
    cognitive: Math.round((sum.cognitive / c) * 10) / 10,
    emotional: Math.round((sum.emotional / c) * 10) / 10,
    logistical: Math.round((sum.logistical / c) * 10) / 10,
    relational: Math.round((sum.relational / c) * 10) / 10,
  }
}

export function computeClarityScore(nodes: AttentionNode[], tensions: number): number {
  // Simple, transparent, non-gamified: more perspectives + recovery on high burden + acknowledged tensions
  const highBurden = nodes.filter(n => n.burden && 
    (n.burden.emotional + n.burden.cognitive + n.burden.logistical) > 18).length
  const withRecovery = nodes.filter(n => n.recovery && n.abandonment).length
  const base = Math.min(100, Math.round(
    (nodes.length * 3) + (withRecovery * 12) - (tensions * 2) + 20
  ))
  return Math.max(12, Math.min(94, base))
}

export function getNodeTypeLabel(t: NodeType) {
  switch (t) {
    case 'perspective': return 'Perspective'
    case 'task': return 'Task'
    case 'need': return 'Need / Vulnerability'
    case 'prediction': return 'Prediction'
    case 'revision': return 'Revision'
    case 'tension': return 'Productive Tension'
  }
}

export function getNodeTypeColor(t: NodeType) {
  switch (t) {
    case 'perspective': return '#6b8aa3'
    case 'task': return '#5c8a6b'
    case 'need': return '#a38a5e'
    case 'prediction': return '#8a6bb3'
    case 'revision': return '#5c8a8a'
    case 'tension': return '#b35c5c'
  }
}

export function shortLabel(s: string, max = 42) {
  if (s.length <= max) return s
  return s.slice(0, max - 1) + '…'
}

// For public patterns (stripped)
export function makePublicSnapshot(nodes: AttentionNode[], relations: any[]): any {
  const counts: any = {}
  const burdens: any = {}
  let abandon: any = {}
  let recovery: any = { low: 0, medium: 0, high: 0 }
  let tensionCount = 0

  nodes.forEach(n => {
    counts[n.type] = (counts[n.type] || 0) + 1
    if (n.burden) {
      if (!burdens[n.type]) burdens[n.type] = { c: 0, cognitive: 0, emotional: 0, logistical: 0, relational: 0 }
      burdens[n.type].c++
      burdens[n.type].cognitive += n.burden.cognitive
      burdens[n.type].emotional += n.burden.emotional
      burdens[n.type].logistical += n.burden.logistical
      burdens[n.type].relational += n.burden.relational
    }
    if (n.abandonment) {
      const s = n.abandonment.stage
      abandon[s] = (abandon[s] || 0) + 1
    }
    if (n.recovery) {
      recovery[n.recovery.clarity] = (recovery[n.recovery.clarity] || 0) + 1
    }
  })

  relations.forEach(r => { if (r.kind === 'tensions_with') tensionCount++ })

  const avg: any = {}
  Object.keys(burdens).forEach(k => {
    const b = burdens[k]
    avg[k] = {
      cognitive: Math.round((b.cognitive / b.c) * 10) / 10,
      emotional: Math.round((b.emotional / b.c) * 10) / 10,
      logistical: Math.round((b.logistical / b.c) * 10) / 10,
      relational: Math.round((b.relational / b.c) * 10) / 10,
    }
  })

  return {
    nodeTypeCounts: counts,
    avgBurdenByType: avg,
    abandonmentStages: abandon,
    tensionCount,
    recoveryClarityDistribution: recovery,
    contributedAt: nowIso(),
  }
}
